<template>
    <div class="single-sign-on-error">
        <HeaderCentered>
            <template #title>
                <h1>{{ $t('login.error.title') }}</h1>
            </template>
            <template #subtitle>
                <h5 class="warning remove-margin-bottom">
                    {{ $t(errorSlugs.message) }}
                </h5>
            </template>
        </HeaderCentered>
        <OtCardContent>
            <div class="single-sign-on-error__explanation">
                <p
                    v-if="errorSlugs.explanation"
                >{{ $t(errorSlugs.explanation) }}</p>

                <i18n tag="p" :path="errorSlugs.verifyText" v-if="errorSlugs.verifyText">
                    <template #account_link>
                        <a
                            class="single-sign-on-error__explanation__link ot-clickable"
                            :href="url"
                            target="_blank"
                        >
                            {{  $t(errorSlugs.verifyLink) }}
                        </a>
                    </template>
                </i18n>
            </div>

            <OtButton
                class="is-small ot-button is-fullwidth"
                @click="onCtaClick"
                :title="errorSlugs.ctaTitle"
            >
                <i class="oti oti-arrow-left ot-button-icon--left is-small"/>
                {{ $t(errorSlugs.ctaText) }}
            </OtButton>
        </OtCardContent>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { rudderAnalyticsTrack } from '../services/util/rudderstack';
import HeaderCentered from '../components/HeaderCentered.vue';
import OtButton from '../components/OtButton.vue';

interface ErrorSlugs {
    ctaText: string;
    ctaTitle: string;
    explanation?: string;
    message: string;
    verifyText?: string;
    verifyLink?: string;
}

@Component({
    components: { OtButton, HeaderCentered },
})
export default class SingleSignOnErrorView extends Vue {

    created(): void {
        rudderAnalyticsTrack('login sso error', {
            provider: this.$route.params.provider,
            message: this.$route.params.message,
        });
    }

    onCtaClick(): void {
        rudderAnalyticsTrack('login sso error try again', {
            provider: this.$route.params.provider,
            message: this.$route.params.message,
        });

        if (this.$route.params.provider === 'beatport' && this.$route.params.message === 'confirm-email') {
            void this.$router.push({
                name: 'login',
                query: {
                    ...this.$route.query,
                    'login_sso[beatport]': 'auto-redirect',
                },
            });

            return;
        }

        void this.$router.push({ name: 'login', query: this.$route.query });
    }

    get errorSlugs(): ErrorSlugs {
        if (this.$route.params.provider === 'beatport' && this.$route.params.message === 'confirm-email') {
            return {
                explanation: 'login.login.sso.beatport.error.confirm_email.explanation',
                ctaText: 'login.login.sso.beatport.error.confirm_email.cta.text',
                ctaTitle: 'login.login.sso.beatport.error.confirm_email.cta.title',
                message: 'login.login.sso.beatport.error.confirm_email.message',
                verifyText: 'login.login.sso.beatport.error.confirm_email.verify.text',
                verifyLink: 'login.login.sso.beatport.error.confirm_email.verify.account_link',
            };
        }

        return {
            ctaText: 'login.login.sso.unknown.error.generic.cta.text',
            ctaTitle: 'login.login.sso.unknown.error.generic.cta.title',
            message: 'login.login.sso.unknown.error.generic.message',
        };
    }

    get url(): string | undefined {
        if (this.$route.params.provider === 'beatport' && this.$route.params.message === 'confirm-email') {
            return 'https://account.beatport.com';
        }

        return '';
    }

}
</script>

<style scoped>
.single-sign-on-error {
    .warning {
        color: var(--ot-color-accent-orange-dark);
        text-align: center;
        margin-bottom: var(--ot-spacing-3xl);

        &.remove-margin-bottom {
            margin-bottom: 0;
        }
    }

    .single-sign-on-error__explanation {
        margin-bottom: var(--ot-spacing-3xl);

        .single-sign-on-error__explanation__link,
        .single-sign-on-error__explanation__link:visited {
            color: var(--ot-color-core-brand);
        }
    }
}
</style>
